/**
 * Task Handlers for EWS Worker
 * This file exports all available task handlers for different types of worker tasks
 */

import {
  CustomPixelPayload,
  WtagEventPayload,
} from '@/destinations/bundles/ews/event_writer_service_api';
import { Logger } from '@/shared/logger/logger';

/**
 * Base type for all worker task payloads
 */
export type WorkerTaskPayload = {
  data: any;
  params: Record<string, string>;
  url: string;
};

/**
 * Typed worker task payload for custom pixel events
 */
export type CustomPixelTaskPayload = {
  data: CustomPixelPayload;
  params: Record<string, string>;
  url: string;
};

/**
 * Typed worker task payload for WTAG events
 */
export type WtagTaskPayload = {
  data: WtagEventPayload;
  params: Record<string, string>;
  url: string;
};

/**
 * Base type for task handler result
 */
export type TaskHandlerResult = {
  _meta?: Record<string, any>;
  data?: any;
  error?: string;
  success: boolean;
};

/**
 * Type definition for a task handler function
 */
export type TaskHandler<T extends WorkerTaskPayload = WorkerTaskPayload> = (
  payload: T,
) => Promise<TaskHandlerResult>;

/**
 * Call an API with the specified method, URL, parameters, and body
 */
async function callAPI(
  method: string,
  url: string,
  params: Record<string, string>,
  body: any,
): Promise<{
  _meta?: Record<string, any>;
  data: any;
  ok: boolean;
  status: number;
}> {
  // Build URL with query parameters
  const queryString = Object.entries(params)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join('&');

  const fullUrl = `${url}${queryString ? '?' + queryString : ''}`;

  try {
    Logger.debug(`Making ${method} request to: ${fullUrl}`);

    const response = await fetch(fullUrl, {
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
      keepalive: true,
      method,
    });

    const responseData = await response.json().catch(() => ({}));

    return {
      data: responseData,
      ok: response.ok,
      status: response.status,
    };
  } catch (error) {
    Logger.error('[EWS Worker] API call failed:', error);
    throw error;
  }
}

/**
 * Retry a function multiple times with exponential backoff
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  delay = 1000,
): Promise<T> {
  let lastError: Error | null = null;
  let retryCount = 0;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();
      // Add metadata about retries if any happened
      if (retryCount > 0 && typeof result === 'object' && result !== null) {
        const resultWithMeta: T & {
          _meta?: { retryCount: number };
        } = { ...result };

        // Add metadata to the result if it's an object
        resultWithMeta._meta = {
          ...resultWithMeta._meta,
          retryCount,
        };

        return resultWithMeta;
      }
      return result;
    } catch (error) {
      retryCount++;
      Logger.warn(`[EWS Worker] Attempt ${attempt} failed:`, error);
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < maxRetries) {
        // Wait before retrying with exponential backoff
        await new Promise(resolve =>
          setTimeout(resolve, delay * Math.pow(2, attempt - 1)),
        );
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-throw-literal
  throw lastError;
}

/**
 * Handler for custom pixel events
 */
export const customPixelHandler: TaskHandler<CustomPixelTaskPayload> = async (
  payload: CustomPixelTaskPayload,
) => {
  try {
    // Logger.info('Processing custom pixel event');

    const apiResponse = await withRetry(() =>
      callAPI('POST', payload.url, payload.params, payload.data.rawCpxPayload),
    );

    const success = apiResponse.ok;

    if (success) {
      // Logger.info('Custom pixel event processed successfully');
    } else {
      Logger.warn('Custom pixel event failed with status', apiResponse.status);
    }

    return {
      _meta: apiResponse._meta,
      data: apiResponse.data,
      success,
    };
  } catch (error) {
    Logger.error('Error processing custom pixel event:', error);

    return {
      error: error instanceof Error ? error.message : String(error),
      success: false,
    };
  }
};

/**
 * Handler for WTAG events
 */
export const wtagHandler: TaskHandler<WtagTaskPayload> = async (
  payload: WtagTaskPayload,
) => {
  try {
    // Logger.info('Processing WTAG event');

    // Ensure eventID is added to eventData if it exists
    if (payload.data.wtagInput?.eventData) {
      payload.data.wtagInput.eventData.eventID = payload.data.eventId;
    }

    const apiResponse = await withRetry(() =>
      callAPI('POST', payload.url, payload.params, payload.data.wtagInput),
    );

    const success = apiResponse.ok;

    if (success) {
      // Logger.info('WTAG event processed successfully');
    } else {
      Logger.warn('WTAG event failed with status', apiResponse.status);
    }

    return {
      _meta: apiResponse._meta,
      data: apiResponse.data,
      success,
    };
  } catch (error) {
    Logger.error('Error processing WTAG event:', error);

    return {
      error: error instanceof Error ? error.message : String(error),
      success: false,
    };
  }
};

/**
 * Handler for test events
 */
export const testHandler: TaskHandler = async (payload: WorkerTaskPayload) => {
  try {
    Logger.info('Processing test event', payload);

    // Simply echo back the payload
    return {
      data: {
        echo: payload.data,
        received: true,
      },
      success: true,
    };
  } catch (error) {
    Logger.error('Error processing test event:', error);

    return {
      error: error instanceof Error ? error.message : String(error),
      success: false,
    };
  }
};

/**
 * Handler for status requests
 */
export const statusHandler: TaskHandler = async () => {
  try {
    Logger.info('Processing status request');

    return {
      data: {
        status: 'healthy',
        timestamp: Date.now(),
      },
      success: true,
    };
  } catch (error) {
    Logger.error('Error processing status request:', error);

    return {
      error: error instanceof Error ? error.message : String(error),
      success: false,
    };
  }
};

/**
 * Map of task handlers for different task types
 */
export const taskHandlers: Record<string, TaskHandler> = {
  GET_STATUS: statusHandler,
  SEND_CUSTOM_PIXEL_EVENT: customPixelHandler,
  SEND_MABLE_EVENT: customPixelHandler,
  SEND_WTAG_EVENT: wtagHandler,
  TEST: testHandler,
};
